import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

import useAnimate from './Motion';

function LitWord({ children, progress, start, end }) {
    const opacity = useTransform(progress, [start, end], [0, 1]);

    return (
        <span className="lit__word">
            <span className="lit__ghost">{children}</span>
            <motion.span className="lit__fill" style={{ opacity }} aria-hidden="true">
                {children}
            </motion.span>
        </span>
    );
}

/**
 * A statement that illuminates word by word as the reader scrolls through it.
 * Each word owns a slice of the section's scroll progress, and the slices
 * overlap slightly so the sweep reads as one continuous wipe.
 */
export default function ScrollLitText({ text, className, targetRef }) {
    const localRef = useRef(null);
    const ref = targetRef || localRef;
    const animate = useAnimate();

    // The ref belongs to the pinned section above us, so it is not attached
    // until after this component's layout effects. Defer measurement a tick.
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start 0.9', 'end 0.55'],
        layoutEffect: false
    });

    const words = text.split(' ');

    if (!animate)
        return <p className={className} ref={localRef}>{text}</p>;

    return (
        <p className={className} ref={targetRef ? undefined : localRef} aria-label={text}>
            {words.map((w, i) => {
                const start = i / words.length;
                const end = Math.min(1, start + 1.9 / words.length);

                return (
                    <LitWord key={`${w}-${i}`} progress={scrollYProgress} start={start} end={end}>
                        {w}
                    </LitWord>
                );
            })}
        </p>
    );
}
